import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { ColorfulLogo } from "@/components/ColorfulLogo";

interface PrivacyModalProps {
  open: boolean;
  onClose: () => void;
}

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: "We collect the following \"Personal Data\" to provide our AI agent services:",
    list: [
      "Identifiers: Name, Email Address.",
      "Professional Information: Company Name.",
      "Payment Information: We do not collect or store credit card numbers. All payments are processed by Stripe, which uses 256-bit SSL encryption to secure your data.",
    ],
  },
  {
    title: "2. How We Use and Share Your Data",
    body: "We process your data solely to:",
    list: [
      "Contact you regarding your inquiry.",
      "Deliver the \"Kickoff\" and implementation phases of your AI agent.",
      "Third-Party Sharing: We only share your data with necessary service providers (like Stripe for payments or communication tools) to fulfill our contract with you. We do not \"sell\" your personal data to third parties as defined under Texas law.",
    ],
  },
  {
    title: "3. Your Rights Under Texas Law (TDPSA)",
    body: "As a Texas resident, you have the following rights regarding your personal data:",
    list: [
      "Right to Access: You can request to see the personal data we have collected about you.",
      "Right to Correction: You can ask us to fix inaccurate or incomplete data.",
      "Right to Deletion: You can request that we delete the personal data we have collected.",
      "Right to Data Portability: You can request a copy of your data in a digital, portable format.",
      "Right to Opt-Out: You have the right to opt-out of the \"sale\" of your data or its use for targeted advertising. Note: AgentsToGo does not currently engage in these activities.",
    ],
  },
  {
    title: "4. How to Exercise Your Rights & Appeal Process",
    body: "To exercise any of these rights, please email us at info@bluepeople.com. We will respond to your request within 45 days.",
    list: null,
  },
  {
    title: "5. Data Security",
    body: "We maintain reasonable administrative and technical safeguards to protect your information. As a service powered by Blue People, we utilize industry-standard security protocols to ensure your data is handled safely.",
    list: null,
  },
];

export function PrivacyModal({ open, onClose }: PrivacyModalProps) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="privacy-modal"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-50 scifi-wave-bg overflow-y-auto"
        >
          {/* Sticky nav */}
          <div className="sticky top-0 z-[200] bg-black/70 backdrop-blur-md border-b border-cyan-400/15 flex items-center justify-between px-6 sm:px-12 py-4">
            <ColorfulLogo className="text-base font-bold" />
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300 transition-colors"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Close</span>
            </button>
          </div>

          {/* Hero */}
          <div className="relative border-b border-cyan-400/15 overflow-hidden">
            <div className="absolute -top-16 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative max-w-3xl mx-auto px-6 sm:px-12 py-14 sm:py-20">
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
                className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300 mb-4"
              >
                agentstogo.com
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.14 }}
                className="text-3xl sm:text-5xl font-bold text-white tracking-tight leading-tight mb-4"
              >
                Privacy Policy
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-sm text-slate-400 font-medium"
              >
                Last Updated: May 12, 2026
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.24 }}
                className="mt-4 text-base text-slate-300 max-w-xl leading-relaxed"
              >
                Learn how we handle your personal information and ensure your privacy and data security on our platform.
              </motion.p>
            </div>
          </div>

          {/* Sections */}
          <div className="max-w-3xl mx-auto px-6 sm:px-12 py-12 space-y-10 bg-black/30">
            {SECTIONS.map((section, i) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: 0.04 * i }}
                className="border-b border-cyan-400/10 pb-10 last:border-0"
              >
                <h2 className="text-lg sm:text-xl font-semibold text-white mb-3">
                  {section.title}
                </h2>
                <p className="text-slate-300 leading-relaxed text-[15px]">{section.body}</p>
                {section.list && (
                  <ol className="mt-4 space-y-2 list-decimal list-inside marker:text-cyan-300">
                    {section.list.map((item) => (
                      <li key={item} className="text-slate-300 text-[15px] leading-relaxed pl-1">
                        {item}
                      </li>
                    ))}
                  </ol>
                )}
              </motion.div>
            ))}
          </div>

          {/* Footer strip */}
          <div className="bg-black/60 border-t border-cyan-400/15 px-6 sm:px-12 py-8 text-center">
            <p className="text-xs text-slate-400 mb-3">
              Questions about this policy?
            </p>
            <a
              href="mailto:info@bluepeople.com"
              className="inline-flex items-center gap-2 text-sm font-medium text-cyan-300 hover:text-cyan-200 hover:underline transition-colors"
            >
              info@bluepeople.com
            </a>
            <p className="text-xs text-slate-500 mt-6">
              AgentsToGo · {new Date().getFullYear()}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
